import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { listPlanningCycles,
  currentCycleId, type PlanningCycle } from '../../api/vendorBrandingProfileApi.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface BudgetPlan {
  readonly planId: string;
  readonly planCode: string;
  readonly planName: string;
  readonly financialYear: string;
  /** The cycle the plan was drawn against. Served already; the screen binds the
   *  year picker to it so the year shown is the real one and not a label. */
  readonly planningCycleId: string | null;
  readonly status: string;
  readonly allocatedAmountInr: number;
  readonly consumedAmountInr: number;
  readonly reservedAmountInr: number;
  readonly availableAmountInr: number;
  readonly utilizationPct: number;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly regionCode: string | null;
  readonly territoryCode: string | null;
  readonly approvedAt: string | null;
  readonly approvedByName: string | null;
  readonly version: number;
  readonly fyClosed: boolean;
  readonly editLocked: boolean;
  readonly editLockReason: 'FY_CLOSED' | 'AWAITING_APPROVAL' | 'FROZEN' | null;
}


const CARD: React.CSSProperties = {
  padding: 16, borderRadius: 8, marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};

export function ShBudgetListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money, number } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language
  // picker. `fmtApproved` used to live here and spelled out 'en-IN' with a
  // medium dateStyle, so this screen wrote a date its own way whatever the
  // tenant had chosen; `at` is the one formatter every screen shares.
  const { day, at } = useBusinessDay();
  const [plans, setPlans] = useState<BudgetPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Apart from `error`, which every plan reload clears.
  const [cyclesError, setCyclesError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // Set while modifying: the plan being edited. null means the form creates.
  const [editing, setEditing] = useState<BudgetPlan | null>(null);
  const [changeReason, setChangeReason] = useState('');

  // New plan form
  const [planName, setPlanName] = useState('');
  const [fy, setFy] = useState('');
  // The financial year is chosen from the registry, never typed: a typed year is
  // how the same year ended up spelled two ways.
  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [amount, setAmount] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<BudgetPlan[]>('/v1/budget/plans', { token });
      setPlans(r);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    let alive = true;
    listPlanningCycles(token)
      .then((c) => { if (alive) { setCycles(c); setFy((prev) => prev || currentCycleId(c)); } })
      .catch((e) => { if (alive) setCyclesError(`Financial years could not be loaded: ${(e as Error).message}`); });
    return () => { alive = false; };
  }, [token]);

  const handleCreate = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      if (editing) {
        // One live plan per financial year (t139_plan_dimension_idx), so a
        // revision edits the existing plan instead of adding a second one.
        await apiRequest(`/v1/budget/plans/${encodeURIComponent(editing.planId)}`, {
          method: 'PUT', token,
          body: {
            planName,
            allocatedAmountInr: Number(amount),
            effectiveFromDate: fromDate, effectiveToDate: toDate,
            changeReason: changeReason || undefined,
          },
        });
        setInfo(t('sh.budgetList.modified', { code: editing.planCode }));
      } else {
        await apiRequest('/v1/budget/plans', {
          method: 'POST', token,
          body: {
            planName, planningCycleId: fy,
            allocatedAmountInr: Number(amount),
            effectiveFromDate: fromDate, effectiveToDate: toDate,
          },
        });
        setInfo(t('sh.budgetList.created'));
      }
      setPlanName(''); setFy(''); setAmount(''); setFromDate(''); setToDate('');
      setChangeReason(''); setEditing(null);
      setShowForm(false);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [planName, fy, amount, fromDate, toDate, changeReason, editing, token, busy, load, t]);

  const startModify = useCallback((p: BudgetPlan) => {
    setEditing(p);
    setPlanName(p.planName);
    // Bind the year to the cycle the plan actually carries. The picker used to
    // show the year a second time as plain text, because nothing selected the
    // real one: a year listed twice, where only the entry holding an id does
    // anything.
    setFy(p.planningCycleId ?? '');
    setAmount(String(p.allocatedAmountInr));
    setFromDate(p.effectiveFromDate);
    setToDate(p.effectiveToDate);
    setChangeReason('');
    setShowForm(true);
    setError(null); setInfo(null);
  }, []);

  // Revising something already decided must say why; a draft need not.
  const reasonRequired =
    editing !== null && (editing.status === 'ACTIVE' || editing.status === 'REJECTED');

  return (
    <main aria-label="sh-budget-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20 }}>{t('sh.budgetList.title')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/sh/dashboard" data-testid="back-dashboard">{t('sh.common.dashboard')}</Link>
          <Button type="button" variant="secondary" onClick={() => { setEditing(null); setShowForm(!showForm); }} data-testid="btn-new-plan">
            {t('sh.budgetList.newPlan')}
          </Button>
        </div>
      </div>

      {cyclesError && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{cyclesError}</p>}
      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {/* ── Create form ──────────────────────────────── */}
      {showForm && (
        <section style={CARD} aria-label="new-plan-form">
          <h2 style={{ fontSize: 16, marginBottom: 12 }}>
            {editing ? t('sh.budgetList.modifyHeading', { code: editing.planCode, version: editing.version }) : t('sh.budgetList.newPlanHeading')}
          </h2>
          <form onSubmit={handleCreate} aria-label="create-plan-form">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              <div>
                <label htmlFor="bp-name" style={{ fontSize: 12 }}>{t('sh.budgetList.planName')}</label>
                <input id="bp-name" value={planName} onChange={(e) => setPlanName(e.target.value)} required
                       data-testid="input-plan-name"
                       style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="bp-fy" style={{ fontSize: 12 }}>{t('sh.common.financialYear')}</label>
                <select id="bp-fy" value={fy} onChange={(e) => setFy(e.target.value)} required
                        data-testid="input-fy" disabled={cycles.length === 0 || editing !== null}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                  {cycles.length === 0 && <option value="">No financial year set up</option>}
                  {cycles.map((c) => (
                    <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleCode}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bp-amount" style={{ fontSize: 12 }}>{t('sh.budgetList.allocatedAmount')}</label>
                <input id="bp-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required
                       data-testid="input-amount"
                       style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="bp-from" style={{ fontSize: 12 }}>{t('sh.budgetList.effectiveFrom')}</label>
                <DateInput id="bp-from" type="date" value={fromDate} onValueChange={(nextValue) => setFromDate(nextValue)} required
                       data-testid="input-from"
                       style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
              <div>
                <label htmlFor="bp-to" style={{ fontSize: 12 }}>{t('sh.budgetList.effectiveTo')}</label>
                <DateInput id="bp-to" type="date" value={toDate} onValueChange={(nextValue) => setToDate(nextValue)} required
                       data-testid="input-to"
                       style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
            </div>
            {editing && (
              <div style={{ marginTop: 12 }}>
                <label htmlFor="bp-reason" style={{ fontSize: 12 }}>
                  {t('sh.budgetList.changeReason')}{reasonRequired ? ' *' : ''}
                </label>
                <input id="bp-reason" value={changeReason} onChange={(e) => setChangeReason(e.target.value)}
                       data-testid="input-change-reason" required={reasonRequired}
                       placeholder={reasonRequired ? t('sh.budgetList.changeReasonRequired') : t('sh.budgetList.changeReasonHint')}
                       style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
            )}
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-plan">
                {editing ? t('sh.budgetList.saveVersion') : t('sh.common.create')}
              </Button>
              <Button type="button" variant="danger" onClick={() => { setShowForm(false); setEditing(null); }}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {/* ── Plan list ────────────────────────────────── */}
      {plans.length === 0 && !error ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.budgetList.empty')}</p>
      ) : (
        <DataTable<BudgetPlan>
          ariaLabel="budget-plans-table"
          rows={plans}
          rowKey={(p) => p.planId}
          columns={[
            { key: 'code', header: t('sh.budgetList.colCode'), render: (p) => <code style={{ fontSize: 12 }}>{p.planCode}</code> },
            { key: 'name', header: t('sh.budgetList.colName'), render: (p) => p.planName },
            { key: 'fy', header: t('sh.budgetList.colFy'), render: (p) => p.financialYear },
            { key: 'version', header: t('sh.budgetList.colVersion'), render: (p) => <span data-testid={`version-${p.planId}`}>{p.version > 0 ? t('sh.budgetList.versionLabel', { n: p.version }) : '—'}</span> },
            { key: 'status', header: t('sh.common.status'), render: (p) => p.status },
            { key: 'allocated', header: t('sh.common.allocatedInr'), render: (p) => <>₹{money(p.allocatedAmountInr)}</> },
            { key: 'consumed', header: t('sh.common.consumedInr'), render: (p) => <>₹{money(p.consumedAmountInr)}</> },
            { key: 'available', header: t('sh.budgetList.colAvailable'), render: (p) => <>₹{money(p.availableAmountInr)}</> },
            { key: 'util', header: t('sh.budgetList.colUtil'), render: (p) => <span style={{ color: p.utilizationPct > 80 ? 'var(--color-danger)' : 'inherit' }}>{number(p.utilizationPct)}%</span> },
            { key: 'period', header: t('sh.common.period'), render: (p) => <span style={{ fontSize: 12 }}>{day(p.effectiveFromDate)} – {day(p.effectiveToDate)}</span> },
            { key: 'approvedOn', header: t('sh.budgetList.colApprovedOn'), render: (p) => <span style={{ fontSize: 12 }} data-testid={`approved-on-${p.planId}`}>{at(p.approvedAt)}</span> },
            { key: 'approvedBy', header: t('sh.budgetList.colApprovedBy'), render: (p) => <span style={{ fontSize: 12 }}>{p.approvedByName ?? (p.approvedAt ? '—' : t('sh.budgetList.notApproved'))}</span> },
            { key: 'actions', header: '', render: (p) => <Link to={`/sh/budget/${p.planId}`} data-testid={`open-plan-${p.planId}`}>{t('sh.budgetList.detail')}</Link> },
            { key: 'modify', header: '', render: (p) => (
              p.editLocked
                ? <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }} data-testid={`edit-locked-${p.planId}`}>
                    {t(`sh.budgetList.lock.${p.editLockReason ?? 'FROZEN'}`)}
                  </span>
                : <Button type="button" variant="secondary" onClick={() => startModify(p)} data-testid={`modify-plan-${p.planId}`}>{t('sh.budgetList.modify')}</Button>
            ) },
            { key: 'planning', header: '', render: (p) => <Link to={`/sh/budget/${p.planId}/planning`} data-testid={`open-planning-${p.planId}`}>{t('sh.budgetList.planning')}</Link> },
          ]}
        />
      )}
    </main>
  );
}
